import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { Plus, X } from 'lucide-react';
import { Button } from '../../components/Button';
import { ChoiceGroup, DayPicker } from '../../components/Choice';
import { Field, Input, Select, Textarea } from '../../components/Field';
import { Card, Page } from '../../components/Page';
import { useDb } from '../../db/hooks';
import { useCanWrite } from '../../account/useCanWrite';
import { deleteMedication, saveMedication, useMedications } from '../../data/meds';
import { inputValue, optionalText } from '../../lib/forms';
import { CATEGORY_LABELS, DAY_RULE_LABELS, FORM_LABELS, medTimes, type DayRule, type MedicationRow } from '../../lib/meds';
import { formatDays, parseDays } from '../../lib/schedule';

const optionalDate = z
  .string()
  .refine((value) => value === '' || /^\d{4}-\d{2}-\d{2}$/.test(value), 'Choose a date.')
  .transform((value) => (value === '' ? null : value));

const schema = z
  .object({
    name: z.string().trim().min(1, 'Enter the medicine’s name.').max(80),
    strength: optionalText(40),
    form: z.enum(['tablet', 'capsule', 'injection', 'sachet', 'drops', 'other']),
    dose: optionalText(60),
    category: z.enum(['phosphate_binder', 'esa', 'iron', 'vitamin_d', 'bp', 'diabetes', 'other', '']).transform((value) => (value === '' ? null : value)),
    times: z.array(z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use a time like 08:00.')),
    with_meals: z.boolean(),
    day_rule: z.enum(['every_day', 'dialysis_days', 'non_dialysis_days', 'specific_days']),
    specific_days: z.array(z.enum(['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'])),
    given_at_center: z.boolean(),
    start_date: optionalDate,
    end_date: optionalDate,
    prescribed_by: optionalText(80),
    notes: optionalText(500),
    active: z.boolean()
  })
  .refine((values) => values.day_rule !== 'specific_days' || values.specific_days.length > 0, { message: 'Choose at least one day.', path: ['specific_days'] })
  .refine((values) => !values.start_date || !values.end_date || values.start_date <= values.end_date, { message: 'The end date is before the start date.', path: ['end_date'] });

type FormInput = z.input<typeof schema>;

export function MedicationFormPage() {
  const { id } = useParams();
  const canWrite = useCanWrite();
  const { medications, isLoading } = useMedications();
  if (isLoading) return null;
  const medication = id ? medications.find((item) => item.id === id) : undefined;
  if ((id && !medication) || !canWrite) return <Navigate to="/health?tab=meds" replace />;
  return <MedicationForm medication={medication} />;
}

function MedicationForm({ medication }: { medication?: MedicationRow }) {
  const db = useDb();
  const navigate = useNavigate();
  const [confirm, setConfirm] = useState(false);
  const form = useForm<FormInput, unknown, z.output<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: medication?.name ?? '',
      strength: inputValue(medication?.strength),
      form: medication?.form ?? 'tablet',
      dose: inputValue(medication?.dose),
      category: medication?.category ?? '',
      times: medication ? medTimes(medication) : ['08:00'],
      with_meals: !!medication?.with_meals,
      day_rule: medication?.day_rule ?? 'every_day',
      specific_days: parseDays(medication?.specific_days),
      given_at_center: !!medication?.given_at_center,
      start_date: inputValue(medication?.start_date),
      end_date: inputValue(medication?.end_date),
      prescribed_by: inputValue(medication?.prescribed_by),
      notes: inputValue(medication?.notes),
      active: medication ? !!medication.active : true
    }
  });
  const { errors, isSubmitting } = form.formState;
  const times = form.watch('times');
  const dayRule = form.watch('day_rule');
  const atCenter = form.watch('given_at_center');

  async function submit(values: z.output<typeof schema>) {
    try {
      await saveMedication(db, medication?.id, {
        ...values,
        times: JSON.stringify([...new Set(values.times)].sort()),
        with_meals: values.with_meals ? 1 : 0,
        specific_days: values.day_rule === 'specific_days' ? formatDays(values.specific_days) : null,
        given_at_center: values.given_at_center ? 1 : 0,
        active: values.active ? 1 : 0
      });
      navigate('/health?tab=meds', { replace: true });
    } catch (error) {
      form.setError('root', { message: error instanceof Error ? error.message : String(error) });
    }
  }

  return (
    <Page title={medication ? medication.name : 'Add a medicine'} back="/health?tab=meds">
      <form noValidate onSubmit={form.handleSubmit(submit)} className="space-y-4">
        <Card>
          <div className="space-y-4">
            <Field label="Name" error={errors.name?.message}>
              <Input {...form.register('name')} placeholder="e.g. Sevelamer" />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Strength" error={errors.strength?.message}>
                <Input {...form.register('strength')} placeholder="e.g. 800 mg" />
              </Field>
              <Field label="Form">
                <Select {...form.register('form')}>
                  {Object.entries(FORM_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="How much each time" error={errors.dose?.message}>
                <Input {...form.register('dose')} placeholder="e.g. 2 tablets" />
              </Field>
              <Field label="Type">
                <Select {...form.register('category')}>
                  <option value="">–</option>
                  {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
          </div>
        </Card>

        <Card title="When">
          <div className="space-y-4">
            <label className="flex min-h-11 items-center gap-3 text-sm">
              <input type="checkbox" className="h-5 w-5 accent-[rgb(var(--rf-primary))]" {...form.register('given_at_center')} />
              Given to me at dialysis (e.g. an injection or iron)
            </label>
            {atCenter ? null : (
              <>
                <div>
                  <span className="mb-1 block text-sm font-medium">Times</span>
                  <ul className="space-y-2">
                    {times.map((_, index) => (
                      <li key={index} className="flex gap-2">
                        <Input type="time" aria-label={`Time ${index + 1}`} {...form.register(`times.${index}` as const)} />
                        <Button
                          type="button"
                          variant="secondary"
                          aria-label={`Remove time ${index + 1}`}
                          onClick={() => form.setValue('times', times.filter((__, other) => other !== index))}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </li>
                    ))}
                  </ul>
                  {errors.times?.message || errors.times?.[0]?.message ? <p className="mt-1 text-xs text-clinical-danger">{errors.times?.message ?? errors.times?.[0]?.message}</p> : null}
                  <Button type="button" variant="ghost" className="mt-1" onClick={() => form.setValue('times', [...times, times.length ? '20:00' : '08:00'])}>
                    <Plus className="h-4 w-4" /> Add a time
                  </Button>
                  {!times.length ? <p className="text-xs text-clinical-muted">No times: taken only when needed, so it won’t appear on Today.</p> : null}
                </div>
                <label className="flex min-h-11 items-center gap-3 text-sm">
                  <input type="checkbox" className="h-5 w-5 accent-[rgb(var(--rf-primary))]" {...form.register('with_meals')} />
                  Take with food
                </label>
                <Controller
                  control={form.control}
                  name="day_rule"
                  render={({ field }) => (
                    <ChoiceGroup<DayRule>
                      name="day-rule"
                      value={field.value}
                      onChange={field.onChange}
                      columns={2}
                      options={(Object.keys(DAY_RULE_LABELS) as DayRule[]).map((value) => ({ value, label: DAY_RULE_LABELS[value] }))}
                    />
                  )}
                />
                {dayRule === 'specific_days' ? (
                  <div>
                    <Controller control={form.control} name="specific_days" render={({ field }) => <DayPicker label="Days" value={field.value} onChange={field.onChange} />} />
                    {errors.specific_days?.message ? <p className="mt-1 text-xs text-clinical-danger">{errors.specific_days.message}</p> : null}
                  </div>
                ) : null}
              </>
            )}
            <div className="grid grid-cols-2 gap-3">
              <Field label="Started (optional)" error={errors.start_date?.message}>
                <Input type="date" {...form.register('start_date')} />
              </Field>
              <Field label="Until (optional)" error={errors.end_date?.message}>
                <Input type="date" {...form.register('end_date')} />
              </Field>
            </div>
          </div>
        </Card>

        <Card>
          <div className="space-y-4">
            <Field label="Prescribed by (optional)" error={errors.prescribed_by?.message}>
              <Input {...form.register('prescribed_by')} />
            </Field>
            <Field label="Notes (optional)" error={errors.notes?.message}>
              <Textarea {...form.register('notes')} />
            </Field>
            {medication ? (
              <label className="flex min-h-11 items-center gap-3 text-sm">
                <input type="checkbox" className="h-5 w-5 accent-[rgb(var(--rf-primary))]" {...form.register('active')} />
                I still take this medicine
              </label>
            ) : null}
          </div>
        </Card>

        {errors.root?.message ? <p className="text-sm text-clinical-danger">{errors.root.message}</p> : null}
        <Button type="submit" className="w-full" disabled={isSubmitting}>
          {isSubmitting ? 'Saving…' : 'Save medicine'}
        </Button>
        {medication ? (
          confirm ? (
            <div className="flex items-center gap-2">
              <span className="flex-1 text-sm">Delete this medicine and its history? To keep the history, untick “I still take this medicine” instead.</span>
              <Button type="button" variant="danger" onClick={() => void deleteMedication(db, medication.id).then(() => navigate('/health?tab=meds', { replace: true }))}>
                Delete
              </Button>
            </div>
          ) : (
            <Button type="button" variant="ghost" className="w-full !text-clinical-danger hover:!bg-red-500/10" onClick={() => setConfirm(true)}>
              Delete medicine
            </Button>
          )
        ) : null}
      </form>
    </Page>
  );
}
